import React, { useEffect, useMemo, useState } from 'react';
import { referrals, brands as brandsApi, projects as projectsApi } from '@/utils/api';
import { Gift, Plus, Trash2, X, DollarSign, CheckCircle, Trophy, Building2, Sparkles, Copy as CopyIcon, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Referrals() {
  const [data, setData] = useState({ items: [], stats: { pending_commission: 0, paid: 0, total: 0 } });
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ referrer_name: '', referred_email: '', referred_name: '', brand_id: '', commission_pct: 10, notes: '' });
  const [convertFor, setConvertFor] = useState(null);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiBusy, setAiBusy] = useState(null);

  const reload = () => referrals.list().then(r => setData(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    brandsApi.list().then(r => setBrands(r.data)).catch(() => {});
    projectsApi.list().then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await referrals.create(form);
      toast.success('Referral added');
      setCreating(false);
      setForm({ referrer_name: '', referred_email: '', referred_name: '', brand_id: '', commission_pct: 10, notes: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete referral?')) return;
    await referrals.delete(id);
    reload();
  };

  const convert = async (projId, projValue) => {
    try {
      await referrals.convert(convertFor.id, projId, projValue);
      toast.success('Converted');
      setConvertFor(null);
      reload();
    } catch { toast.error('Failed'); }
  };

  const markPaid = async (id) => {
    await referrals.markPaid(id);
    toast.success('Marked paid');
    reload();
  };

  const draftEmail = async (r) => {
    setAiBusy(r.id);
    try {
      const res = await referrals.aiDraftEmail(r.id);
      setAiDraft({ referral: r, subject: res.data.subject, body: res.data.body });
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); }
    finally { setAiBusy(null); }
  };

  // --- Derived: top referrers & per-brand breakdown ---
  const topReferrers = useMemo(() => {
    const agg = {};
    for (const r of data.items) {
      const key = r.referrer_name || '—';
      if (!agg[key]) agg[key] = { name: key, count: 0, pending: 0, paid: 0, converted: 0 };
      agg[key].count += 1;
      if (r.status === 'converted') agg[key].pending += r.commission_amount || 0;
      if (r.status === 'paid') agg[key].paid += r.commission_amount || 0;
      if (r.status !== 'pending') agg[key].converted += 1;
    }
    return Object.values(agg).sort((a, b) => (b.paid + b.pending) - (a.paid + a.pending)).slice(0, 5);
  }, [data.items]);

  const perBrand = useMemo(() => {
    const agg = {};
    for (const r of data.items) {
      const brand = brands.find(b => b.id === r.brand_id);
      const key = brand?.name || 'No brand';
      if (!agg[key]) agg[key] = { name: key, color: brand?.primary_color || '#8A99A0', count: 0, value: 0 };
      agg[key].count += 1;
      agg[key].value += r.commission_amount || 0;
    }
    return Object.values(agg).sort((a, b) => b.value - a.value);
  }, [data.items, brands]);

  return (
    <div className="p-6 animate-fade-in" data-testid="referrals-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#3C8A5A20' }}>
            <Gift size={18} style={{ color: '#3C8A5A' }} />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Referrals</h1>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>Track introductions, commissions, and payouts</p>
          </div>
        </div>
        <button className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5" onClick={() => setCreating(true)} data-testid="new-referral">
          <Plus size={12} /> Add Referral
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="card-base p-4">
          <p className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Total referrals</p>
          <p className="text-3xl font-black" style={{ color: 'var(--ink)' }}>{data.stats.total}</p>
        </div>
        <div className="card-base p-4">
          <p className="text-xs font-bold uppercase mb-1" style={{ color: '#D9A441' }}>Pending commission</p>
          <p className="text-3xl font-black" style={{ color: '#D9A441' }}>${data.stats.pending_commission.toFixed(2)}</p>
        </div>
        <div className="card-base p-4">
          <p className="text-xs font-bold uppercase mb-1" style={{ color: '#3C8A5A' }}>Paid out</p>
          <p className="text-3xl font-black" style={{ color: '#3C8A5A' }}>${data.stats.paid.toFixed(2)}</p>
        </div>
      </div>

      {/* Leaderboard + Brand breakdown */}
      {data.items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3 mb-5">
          <div className="card-base p-4" data-testid="top-referrers">
            <p className="text-xs font-bold uppercase mb-3 flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
              <Trophy size={11} style={{ color: '#D9A441' }} /> Top referrers
            </p>
            {topReferrers.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--muted)' }}>No data yet.</p>
            ) : (
              <div className="space-y-2">
                {topReferrers.map((r, i) => (
                  <div key={r.name} className="flex items-center gap-3" data-testid={`top-referrer-${i}`}>
                    <span className="font-mono text-xs font-bold w-5" style={{ color: i === 0 ? '#D9A441' : 'var(--muted)' }}>#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: 'var(--ink)' }}>{r.name}</p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{r.count} intro{r.count > 1 ? 's' : ''} · {r.converted} converted</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {r.pending > 0 && <p className="text-xs font-mono font-bold" style={{ color: '#D9A441' }}>${r.pending.toFixed(0)} pending</p>}
                      {r.paid > 0 && <p className="text-xs font-mono font-bold" style={{ color: '#3C8A5A' }}>${r.paid.toFixed(0)} paid</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="card-base p-4" data-testid="per-brand-breakdown">
            <p className="text-xs font-bold uppercase mb-3 flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
              <Building2 size={11} /> Per-brand breakdown
            </p>
            {perBrand.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--muted)' }}>No data yet.</p>
            ) : (
              <div className="space-y-2">
                {perBrand.map(b => (
                  <div key={b.name} className="flex items-center gap-2" data-testid={`brand-row-${b.name}`}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                    <span className="text-sm flex-1 truncate" style={{ color: 'var(--ink)' }}>{b.name}</span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{b.count} intro{b.count > 1 ? 's' : ''}</span>
                    <span className="text-xs font-mono font-bold tabular-nums" style={{ color: 'var(--ink)' }}>${b.value.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="card-base p-10 text-center">
          <Gift size={30} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No referrals yet</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="referrals-list">
          {data.items.map(r => (
            <div key={r.id} className="card-base p-3" data-testid={`referral-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{r.referred_name || r.referred_email}</p>
                    <span className="badge-status" style={{
                      background: r.status === 'paid' ? '#3C8A5A15' : r.status === 'converted' ? '#D9A44115' : '#8A99A015',
                      color: r.status === 'paid' ? '#3C8A5A' : r.status === 'converted' ? '#D9A441' : '#5C6B72',
                    }}>{r.status}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>Referred by <b>{r.referrer_name}</b> · {r.commission_pct}% commission</p>
                  {r.commission_amount > 0 && <p className="text-xs mt-0.5" style={{ color: '#3C8A5A' }}>Commission: ${r.commission_amount.toFixed(2)}</p>}
                </div>
                <div className="flex gap-1">
                  {r.status === 'pending' && (
                    <button onClick={() => setConvertFor(r)} className="text-xs px-2 py-1 rounded-btn font-semibold" style={{ background: '#006080', color: 'white' }} data-testid={`convert-${r.id}`}>Convert</button>
                  )}
                  {r.status === 'converted' && (
                    <button onClick={() => markPaid(r.id)} className="text-xs px-2 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A', color: 'white' }}><CheckCircle size={10} /> Mark Paid</button>
                  )}
                  <button onClick={() => draftEmail(r)} disabled={aiBusy === r.id} className="text-xs px-2 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#5B2D8E15', color: '#5B2D8E' }} title="AI-draft a follow-up email to the referrer" data-testid={`ai-draft-${r.id}`}>
                    <Sparkles size={10} /> {aiBusy === r.id ? '…' : 'Draft'}
                  </button>
                  <button onClick={() => del(r.id)} className="text-xs p-1" style={{ color: '#B34A3A' }}><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-lg rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>New Referral</h2>
              <button onClick={() => setCreating(false)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
            </div>
            <div className="space-y-2">
              <input className="input-agency text-sm" placeholder="Referrer name" value={form.referrer_name} onChange={e => setForm({...form, referrer_name: e.target.value})} data-testid="ref-referrer" />
              <input className="input-agency text-sm" type="email" placeholder="Referred email" value={form.referred_email} onChange={e => setForm({...form, referred_email: e.target.value})} data-testid="ref-email" />
              <input className="input-agency text-sm" placeholder="Referred name (optional)" value={form.referred_name} onChange={e => setForm({...form, referred_name: e.target.value})} />
              <select className="input-agency text-sm" value={form.brand_id} onChange={e => setForm({...form, brand_id: e.target.value})} data-testid="ref-brand">
                <option value="">Brand</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <input className="input-agency text-sm" type="number" step="0.5" placeholder="Commission %" value={form.commission_pct} onChange={e => setForm({...form, commission_pct: parseFloat(e.target.value)})} />
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              <div className="flex gap-2 pt-2">
                <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setCreating(false)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
                <button className="flex-1 btn-primary py-2 text-sm" onClick={save} data-testid="ref-save">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {convertFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <h3 className="font-black mb-3" style={{ color: 'var(--ink)' }}>Convert referral</h3>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>Link a project + value. Commission = {convertFor.commission_pct}% of value.</p>
            <ConvertForm onConvert={convert} projects={projects} />
            <button className="w-full py-2 text-sm rounded-btn mt-2 border" onClick={() => setConvertFor(null)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* AI-drafted email viewer */}
      {aiDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="ai-draft-modal">
          <div className="w-full max-w-lg rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: '#5B2D8E' }} />
                <h2 className="font-black text-base" style={{ color: 'var(--ink)' }}>AI-drafted email</h2>
              </div>
              <button onClick={() => setAiDraft(null)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
              To: <strong>{aiDraft.referral.referrer_name}</strong> — review, edit if you want, and send via your email client.
            </p>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Subject</label>
            <input className="input-agency text-sm mb-2" value={aiDraft.subject} onChange={e => setAiDraft({ ...aiDraft, subject: e.target.value })} />
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Body</label>
            <textarea className="input-agency text-sm resize-none mb-3" rows={10} value={aiDraft.body} onChange={e => setAiDraft({ ...aiDraft, body: e.target.value })} data-testid="ai-draft-body" />
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(`Subject: ${aiDraft.subject}\n\n${aiDraft.body}`); toast.success('Copied to clipboard'); }} className="flex-1 py-2 text-sm rounded-btn flex items-center justify-center gap-1.5" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }} data-testid="ai-draft-copy">
                <CopyIcon size={12} /> Copy
              </button>
              <a
                href={`mailto:${encodeURIComponent(aiDraft.referral.referrer_name || '')}?subject=${encodeURIComponent(aiDraft.subject)}&body=${encodeURIComponent(aiDraft.body)}`}
                className="flex-1 py-2 text-sm rounded-btn text-white font-semibold flex items-center justify-center gap-1.5"
                style={{ background: '#5B2D8E' }}
                onClick={() => setAiDraft(null)}
              >
                <Send size={12} /> Open in mail client
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConvertForm({ projects, onConvert }) {
  const [pid, setPid] = useState('');
  const [val, setVal] = useState('');
  return (
    <div className="space-y-2">
      <select className="input-agency text-sm" value={pid} onChange={e => setPid(e.target.value)} data-testid="convert-project">
        <option value="">Pick project…</option>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <input className="input-agency text-sm" type="number" placeholder="Project value ($)" value={val} onChange={e => setVal(e.target.value)} data-testid="convert-value" />
      <button className="btn-primary w-full py-2 text-sm flex items-center justify-center gap-1.5" onClick={() => onConvert(pid, parseFloat(val || '0'))} disabled={!pid || !val} data-testid="convert-submit">
        <DollarSign size={12} /> Convert
      </button>
    </div>
  );
}
